import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  GenericResponse,
  CommonResponse,
  Sortable,
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
} from "./utils";

export interface SystemResourceService {
  listSystemResource: ReturnType<typeof listSystemResource>;
  getSystemResourceDetail: ReturnType<typeof getSystemResourceDetail>;
  putSystemResourceDetail: ReturnType<typeof putSystemResourceDetail>;
  postSystemResourceDetail: ReturnType<typeof postSystemResourceDetail>;
  deleteSystemResourceDetail: ReturnType<typeof deleteSystemResourceDetail>;
}

export type SystemResourceProvider = "database" | "aws-s3";
interface SystemResourceSearchCriteria extends Sortable {
  id?: number;
  tenantId?: string;
  provider?: SystemResourceProvider[];
  category?: string;
  resourceNames?: string[];
  originalFileName?: string;
  "createTime.from"?: string;
  "createTime.to"?: string;
  createAdmin?: string;
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
  lastUpdateAdmin?: string;
}

export type SystemResourceType = "SYSTEM" | "USER";
export interface ResourceDTO {
  id: number;
  tenantId: string;
  type: SystemResourceType;
  userId: string;
  provider: SystemResourceProvider;
  category: string;
  resourceName?: string;
  originalFileName: string;
  fileMimetype?: string;
  remark?: string;
  createTime: string;
  createAdmin: string;
  lastUpdateTime: string;
  lastUpdateAdmin: string;
}

export interface ResourceDetail extends ResourceDTO {
  encodedContent: string;
  annotoation?: any[];
  purged: boolean;
}

interface ResourceFileUpload {
  fileName: string;
  fileMimetype: string;
  encodedContent: string;
}

export interface ResourceData {
  provider?: SystemResourceProvider
  remark?: string;
  fileupload?: ResourceFileUpload;
}
export interface ResourceRequestDetail {
  category: string;
  resourceKey: string;
  detail: ResourceData;
}
export interface ResourceRequest {
  provider?: SystemResourceProvider;
  encodedContent?: string;
  fileName?: string;
  fileMimetype?: string;
  remark?: string;
}

export const listSystemResource: WrappedCashifyPageableQuery<
  SystemResourceSearchCriteria,
  ResourceDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/resources/system`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getSystemResourceDetail: WrappedCashifyFunction<
  { category: string; resourceKey: string },
  GenericResponse<ResourceDetail>
> = ({ urlPrefix, options }) => {
  return async ({ category, resourceKey }) => {
    let url = `${urlPrefix}/api/v1/resources/system/${category}/${resourceKey}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

const getSystemResourceBody = (detail: ResourceData): ResourceRequest => {
  let { provider, remark, fileupload } = detail;
  let body: ResourceRequest = {};
  if (provider !== undefined && provider !== null) {
    body["provider"] = provider;
  }
  if (remark !== undefined && remark !== null) {
    body["remark"] = remark;
  }
  if (fileupload !== undefined && fileupload !== null) {
    body["fileName"] = fileupload.fileName;
    body["fileMimetype"] = fileupload.fileMimetype;
    body["encodedContent"] = fileupload.encodedContent;
  }
  return body;
};

export const putSystemResourceDetail: WrappedCashifyFunction<
  ResourceRequestDetail,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ detail, category, resourceKey }) => {
    let body = getSystemResourceBody(detail);
    let url = `${urlPrefix}/api/v1/resources/system/${category}/${resourceKey}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const postSystemResourceDetail: WrappedCashifyFunction<
  ResourceRequestDetail,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ detail, category, resourceKey }) => {
    let body = getSystemResourceBody(detail);
    let url = `${urlPrefix}/api/v1/resources/system/${category}/${resourceKey}`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteSystemResourceDetail: WrappedCashifyFunction<
  { category: string; resourceKey: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ category, resourceKey }) => {
    let url = `${urlPrefix}/api/v1/resources/system/${category}/${resourceKey}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
